import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Autocomplete from '@mui/material/Autocomplete';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ContentCopy from '@mui/icons-material/ContentCopy';
import VpnKey from '@mui/icons-material/VpnKey';
import Refresh from '@mui/icons-material/Refresh';
import Delete from '@mui/icons-material/Delete';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CategoryIcon from '@mui/icons-material/Category';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from 'recharts';

import { getSessionIdToken } from './auth/apiAuth';
import AlertDialog from './utility/AlertDialog';
import { getFileFromS3 } from './helpers/operations';
import Primarytheme from './utility/theme';

import axios from 'axios';

const {
    REACT_APP_USER_ID,
} = process.env;

const monthLabel = {
    1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
    7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
};

const maskApiKey = (value) => {
    if (!value || value.length < 8) return '••••••••';
    return value.substring(0, 4) + '••••••••••••••••••••' + value.substring(value.length - 4);
};

const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '-';
    const diff = Math.floor((endTime - startTime) / 1000);
    if (diff < 0) return '-';
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getStatusColor = (status) => {
    if (!status) return 'default';
    const s = status.toLowerCase();
    if (s.includes('complete')) return 'success';
    if (s.includes('failed') || s.includes('error')) return 'error';
    if (s.includes('processing') || s.includes('running') || s.includes('paginating')) return 'warning';
    return 'default';
};

// ---------------------------------------------------------------------------
// Sotto-componente: singola riga log espandibile
// ---------------------------------------------------------------------------
function LogEntry({ item, user, onCopy }) {
    const [expanded, setExpanded] = useState(false);
    const isCompleted = (item.status || '').toLowerCase().includes('complete');

    return (
        <Paper
            variant="outlined"
            sx={{
                mb: 1,
                borderLeft: 4,
                borderLeftColor: isCompleted
                    ? 'success.main'
                    : (item.status || '').toLowerCase().includes('fail') || (item.status || '').toLowerCase().includes('error')
                        ? 'error.main'
                        : 'warning.main',
            }}
        >
            {/* --- Riga compatta (sempre visibile) --- */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ px: 2, py: 1, cursor: 'pointer' }}
                onClick={() => setExpanded(!expanded)}
            >
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>
                    {item.startTime ? new Date(item.startTime).toLocaleString() : '-'}
                </Typography>

                <Chip
                    label={item.status || 'unknown'}
                    size="small"
                    color={getStatusColor(item.status)}
                    variant="outlined"
                    sx={{ minWidth: 100 }}
                />

                <Chip label={item.project_id} size="small" variant="outlined" sx={{ maxWidth: 250 }} />

                <Typography variant="body2" noWrap sx={{ flex: 1, color: 'text.secondary' }}>
                    {item.originShort || '-'}
                </Typography>

                <IconButton size="small">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Stack>

            {/* --- Dettagli espandibili --- */}
            <Collapse in={expanded}>
                <Divider />
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={4} flexWrap="wrap">
                            <DetailField label="reqId" value={item.id} mono copyable onCopy={onCopy} />
                            <DetailField label="project_id" value={item.project_id} copyable onCopy={onCopy} />
                            <DetailField label="Duration" value={formatDuration(item.startTime, item.paginationEndTime)} />
                            <DetailField label="Source data" value={item.originShort || item.origin || '-'} />
                        </Stack>

                        {/* Bottoni download */}
                        {isCompleted && (
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                {item.mainPdf && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<PictureAsPdf />}
                                        sx={{
                                            color: 'neutral.main',
                                            borderColor: 'neutral.main',
                                            '&:hover': {
                                                borderColor: 'neutral.dark',
                                                color: 'neutral.dark',
                                                backgroundColor: 'rgba(143, 143, 143, 0.04)',
                                            }
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            getFileFromS3({
                                                key: item.mainPdf,
                                                bucket: item.bucket,
                                                customBucket: item.customBucket,
                                                ssmParams: user.ssmParams,
                                            });
                                        }}
                                    >
                                        View PDF
                                    </Button>
                                )}
                                {item.mainDownload && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        sx={{
                                            color: 'neutral.main',
                                            borderColor: 'neutral.main',
                                            '&:hover': {
                                                borderColor: 'neutral.dark',
                                                color: 'neutral.dark',
                                                backgroundColor: 'rgba(143, 143, 143, 0.04)',
                                            }
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            getFileFromS3({
                                                key: item.mainDownload,
                                                bucket: item.bucket,
                                                customBucket: item.customBucket,
                                                attachment: true,
                                                ssmParams: user.ssmParams,
                                            });
                                        }}
                                    >
                                        Download Export
                                    </Button>
                                )}
                                {item.origin && !item.disableDatafile && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<DescriptionIcon />}
                                        sx={{
                                            color: 'neutral.main',
                                            borderColor: 'neutral.main',
                                            '&:hover': {
                                                borderColor: 'neutral.dark',
                                                color: 'neutral.dark',
                                                backgroundColor: 'rgba(143, 143, 143, 0.04)',
                                            }
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            getFileFromS3({
                                                key: `${item.project_id}/${item.origin}`,
                                                bucket: item.bucket,
                                                attachment: true,
                                                ssmParams: user.ssmParams,
                                            });
                                        }}
                                    >
                                        Source Data
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Box>
            </Collapse>
        </Paper>
    );
}

function DetailField({ label, value, mono = false, copyable = false, onCopy }) {
    return (
        <Box sx={{ minWidth: 120 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2" sx={mono ? { fontFamily: 'monospace', fontSize: '0.8rem' } : {}}>
                    {value || '-'}
                </Typography>
                {copyable && value && (
                    <Tooltip title="Copy">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(value);
                                onCopy && onCopy(value);
                            }}
                            sx={{ p: 0.25 }}
                        >
                            <ContentCopy sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Componente principale
// ---------------------------------------------------------------------------
export default function PaginationAPI({ auth }) {
    // API Key state
    const [loading, setLoading] = useState(true);
    const [apiKeyData, setApiKeyData] = useState(null);
    const [showValue, setShowValue] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [actionLoading, setActionLoading] = useState(false);

    // Project classes
    const [projectClasses, setProjectClasses] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(false);

    // History state
    const [apiProjectIds, setApiProjectIds] = useState(auth.userState.api_project_ids || []);
    const [plotData, setPlotData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [fetchingHistory, setFetchingHistory] = useState(false);

    // Filtro progetto + ricerca + paginazione log
    const [projectFilter, setProjectFilter] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const pageSize = 10;

    // ------------------------------------------------------------------
    // API Key actions
    // ------------------------------------------------------------------

    const callUsersEndpoint = async (action) => {
        const token = await getSessionIdToken();
        return axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/users`,
            { action },
            {
                headers: {
                    'user_id': REACT_APP_USER_ID || auth.userState.ssmParams.USER_ID,
                    'content-type': 'application/json',
                    'Authorization': token,
                },
            }
        );
    };

    const fetchApiKeyInfo = async () => {
        setLoading(true);
        try {
            const resp = await callUsersEndpoint('get');
            const data = resp.data;
            setApiKeyData(data);
        } catch (err) {
            console.error('Error fetching API key info');
            setSnackbar({ open: true, message: 'Error fetching API key info', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        setActionLoading(true);
        try {
            const resp = await callUsersEndpoint('create');
            const data = resp.data;
            setApiKeyData({
                exists: true,
                apiKey: {
                    id: data.id,
                    name: data.name,
                    value: data.value,
                    createdDate: new Date().toISOString(),
                    enabled: true,
                },
                apiProjectIds: apiProjectIds,
            });
            setShowValue(true);
            setSnackbar({ open: true, message: 'API key generated', severity: 'success' });
        } catch (err) {
            console.error('Error generating API key');
            setSnackbar({ open: true, message: 'Error generating API key', severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegenerate = async () => {
        await handleGenerate();
    };

    const handleDelete = async () => {
        setActionLoading(true);
        try {
            await callUsersEndpoint('delete');
            setApiKeyData({ exists: false, apiKey: null, apiProjectIds });
            setShowValue(false);
            setSnackbar({ open: true, message: 'API key deleted successfully', severity: 'success' });
        } catch (err) {
            console.error('Error deleting API key');
            setSnackbar({ open: true, message: 'Error deleting API key', severity: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleCopy = () => {
        if (apiKeyData?.apiKey?.value) {
            navigator.clipboard.writeText(apiKeyData.apiKey.value);
            setSnackbar({ open: true, message: 'API key copied to clipboard', severity: 'success' });
        }
    };

    const fetchProjectClasses = async () => {
        setLoadingClasses(true);
        try {
            const resp = await callUsersEndpoint('getProjectClasses');
            setProjectClasses(resp.data.projectClasses || []);
            //console.log('projectClasses', projectClasses)
        } catch (err) {
            console.error('Error fetching project classes');
        } finally {
            setLoadingClasses(false);
        }
    };

    // ------------------------------------------------------------------
    // History: recupero operazioni per project_id API
    // ------------------------------------------------------------------

    const fetchHistory = async (projectIds) => {
        if (!projectIds || projectIds.length === 0) {
            setPlotData([]);
            setTableData([]);
            return;
        }

        setFetchingHistory(true);
        const token = await getSessionIdToken();

        const plotDataTemp = [];
        const promises = [];

        for (let t = 0; t < 13; t++) {
            const date = new Date();
            date.setMonth(date.getMonth() - t);

            const month = monthLabel[date.getMonth() + 1];
            const year = date.getFullYear();
            const label = `${month} ${year}`;
            plotDataTemp.push({ name: label, completed: 0, failed: 0 });

            const datestart = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
            const dateend = t === 0
                ? new Date().getTime()
                : new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime();

            promises.push(
                axios({
                    method: 'get',
                    url: `${process.env.REACT_APP_API_BASE_URL}/operations`,
                    headers: {
                        'user_id': REACT_APP_USER_ID || auth.userState.ssmParams.USER_ID,
                        'content-type': 'application/json',
                        'Authorization': token,
                        'is_admin': 'false',
                        'project_ids': projectIds.join('@'),
                        'datestart': datestart,
                        'dateend': dateend,
                        'stati': 'tutti',
                        'filter_user': false,
                    },
                })
                    .then(resp => ({ label, items: resp.data.items || [] }))
                    .catch(err => {
                        console.error('Error fetching History');
                        return { label, items: [] };
                    })
            );
        }

        try {
            const responses = await Promise.all(promises);
            const allItems = [];

            responses.forEach(({ label, items }) => {
                let completedCount = 0;
                let failedCount = 0;

                items.forEach(proc => {
                    const status = (proc.status || '').toLowerCase();
                    if (status.includes('complete')) completedCount++;
                    else if (status.includes('failed') || status.includes('error')) failedCount++;

                    allItems.push({
                        id: proc.reqId,
                        project_id: proc.project_id,
                        status: proc.status,
                        startTime: proc.startTime,
                        paginationEndTime: proc.paginationEndTime,
                        originShort: proc.originShort || '',
                        origin: proc.origin || '',
                        // Campi download (nomi reali dall'endpoint /operations)
                        mainPdf: proc.mainPdf,
                        mainDownload: proc.mainDownload,
                        bucket: proc.bucket,
                        customBucket: proc.customBucket,
                        disableDatafile: proc.disableDatafile,
                    });
                });

                const idx = plotDataTemp.findIndex(m => m.name === label);
                if (idx !== -1) {
                    plotDataTemp[idx].completed = completedCount;
                    plotDataTemp[idx].failed = failedCount;
                }
            });

            allItems.sort((a, b) => (b.startTime || 0) - (a.startTime || 0));

            setPlotData(plotDataTemp.reverse());
            setTableData(allItems);
        } catch (err) {
            console.error('Error fetching History:');
        } finally {
            setFetchingHistory(false);
        }
    };

    // ------------------------------------------------------------------
    // Effects
    // ------------------------------------------------------------------

    useEffect(() => {
        fetchApiKeyInfo();
        //console.log('user', auth)
    }, []);

    useEffect(() => {
        if (apiProjectIds.length > 0) {
            fetchHistory(apiProjectIds);
        }
    }, [apiProjectIds]);

    useEffect(() => {
        fetchApiKeyInfo();
        fetchProjectClasses();
    }, []);

    // ------------------------------------------------------------------
    // Filtro locale sulla lista log
    // ------------------------------------------------------------------

    const filteredTableData = useMemo(() => {
        setPage(0);
        let data = tableData;
        if (projectFilter.length > 0) {
            data = data.filter(item => projectFilter.includes(item.project_id));
        }
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            data = data.filter(item =>
                (item.id || '').toLowerCase().includes(q) ||
                (item.project_id || '').toLowerCase().includes(q) ||
                (item.status || '').toLowerCase().includes(q) ||
                (item.originShort || '').toLowerCase().includes(q) ||
                (item.origin || '').toLowerCase().includes(q)
            );
        }
        return data;
    }, [tableData, projectFilter, searchQuery]);

    // Ricalcola dati grafico in base al filtro progetto
    const filteredPlotData = useMemo(() => {
        if (projectFilter.length === 0) return plotData;

        return plotData.map(monthEntry => {
            const matching = filteredTableData.filter(item => {
                if (!item.startTime) return false;
                const d = new Date(item.startTime);
                const label = `${monthLabel[d.getMonth() + 1]} ${d.getFullYear()}`;
                return label === monthEntry.name;
            });
            let completed = 0;
            let failed = 0;
            matching.forEach(item => {
                const s = (item.status || '').toLowerCase();
                if (s.includes('complete')) completed++;
                else if (s.includes('failed') || s.includes('error')) failed++;
            });
            return { ...monthEntry, completed, failed };
        });
    }, [plotData, filteredTableData, projectFilter]);

    const totalPages = Math.ceil(filteredTableData.length / pageSize);
    const pagedData = filteredTableData.slice(page * pageSize, (page + 1) * pageSize);

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------

    return (
        <Box sx={{ p: '2em' }}>
            {/* ============================================================ */}
            {/* SEZIONE 1: API Key Management                                */}
            {/* ============================================================ */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: '10px' }}>
                <Typography variant='h5'
                    color={Primarytheme.palette.primary.main}
                    sx={{ marginBottom: '1em' }}>
                    <b>API Key</b>
                </Typography>
                <Typography variant="body2" color={Primarytheme.palette.neutral.main} sx={{ mb: 2 }}>
                    Use your API key to access the Pagination API.{' '}
                    <a href="https://pagination.com/api-v2/" color={Primarytheme.palette.neutral.main} target="_blank" rel="noopener noreferrer">
                        View API documentation
                    </a>
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : !apiKeyData?.exists ? (
                    <Box>
                        <Typography sx={{ mb: 2 }} color={Primarytheme.palette.error.main}>
                            No API key found. Generate one to start using the Pagination API.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<VpnKey />}
                            onClick={handleGenerate}
                            disabled={actionLoading}
                        >
                            {actionLoading ? <CircularProgress size={24} /> : 'Generate API Key'}
                        </Button>
                    </Box>
                ) : (
                    <Box>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={showValue ? (apiKeyData.apiKey.value || '') : maskApiKey(apiKeyData.apiKey.value)}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <VpnKey color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={showValue ? 'Hide' : 'Show'}>
                                                <IconButton onClick={() => setShowValue(!showValue)} edge="end" size="small">
                                                    {showValue ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Copy">
                                                <IconButton onClick={handleCopy} edge="end" size="small">
                                                    <ContentCopy />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{
                                mb: 1.5,
                                maxWidth: 600,
                                '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.9rem' },
                            }}
                        />

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            {apiKeyData.apiKey.createdDate && (
                                <Typography variant="body2" color={Primarytheme.palette.neutral.main}>
                                    Created: {new Date(apiKeyData.apiKey.createdDate).toLocaleDateString()}
                                </Typography>
                            )}
                            {apiKeyData.apiKey.enabled && (
                                <Chip label="Active" color='success' size="small" />
                            )}
                        </Stack>

                        <Stack direction="row" spacing={2}>
                            <AlertDialog
                                handleActionOnAgree={handleRegenerate}
                                title="Regenerate API Key"
                                text="This will delete the current API key and generate a new one. Any integration using the old key will stop working. Continue?"
                                buttonProps={{
                                    label: 'Regenerate',
                                    variant: 'contained',
                                    startIcon: <Refresh />,
                                    disabled: actionLoading,
                                }}
                            />
                            <AlertDialog
                                handleActionOnAgree={handleDelete}
                                title="Delete API Key"
                                text="This will permanently delete your API key. Any integration using it will stop working. Continue?"
                                buttonProps={{
                                    label: 'Delete',
                                    variant: 'contained',
                                    color: 'error',
                                    startIcon: <Delete />,
                                    disabled: actionLoading,
                                }}
                            />
                        </Stack>
                    </Box>
                )}
                {/* ---------------------------------------------------------- */}
                {/* Project Classes disponibili                                 */}
                {/* ---------------------------------------------------------- */}
                {!loading && (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <CategoryIcon fontSize="small" color="action" />
                            <Typography variant="subtitle1" color={Primarytheme.palette.neutral.dark}>
                                Available Project Classes
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color={Primarytheme.palette.neutral.main} sx={{ mb: 2 }}>
                            Use these values as <code>project_class</code> when calling{' '}
                            <code>PUT /projects</code> to create a new project.
                        </Typography>

                        {loadingClasses ? (
                            <CircularProgress size={20} />
                        ) : projectClasses.length === 0 ? (
                            <Typography variant="body2" color={Primarytheme.palette.neutral.main}>
                                No project classes available for your account.
                            </Typography>
                        ) : (
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {projectClasses.map(pc => (
                                    <Chip
                                        key={pc.project_class}
                                        label={pc.project_class}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            navigator.clipboard.writeText(pc.project_class);
                                            setSnackbar({ open: true, message: `Copied: ${pc.project_class}`, severity: 'success' });
                                        }}
                                        deleteIcon={<ContentCopy sx={{ fontSize: '14px !important' }} />}
                                        onDelete={() => {
                                            navigator.clipboard.writeText(pc.project_class);
                                            setSnackbar({ open: true, message: `Copied: ${pc.project_class}`, severity: 'success' });
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))}
                            </Stack>
                        )}
                    </>
                )}
            </Paper>

            {/* ============================================================ */}
            {/* SEZIONE 2: API Request History                               */}
            {/* ============================================================ */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: '10px' }}>
                {/* Header con titolo, filtro progetto e refresh */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant='h5'
                        color={Primarytheme.palette.primary.main}
                        sx={{ marginBottom: '1em' }}>
                        <b>API Request History</b>
                    </Typography>

                    {apiProjectIds.length > 0 && (
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                            <TextField
                                variant="standard"
                                size="small"
                                placeholder="Search by reqId, project, status, source data..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchQuery ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearchQuery('')}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                                sx={{ p: 1, minWidth: 420 }}
                            />
                            {apiProjectIds.length > 1 && (
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={apiProjectIds}
                                    value={projectFilter}
                                    onChange={(_, values) => setProjectFilter(values)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Filter by project" placeholder="All projects" />
                                    )}
                                    sx={{ minWidth: 280 }}
                                />
                            )}
                            <Box sx={{ position: 'relative' }}>
                                <Tooltip title="Reload history">
                                    <IconButton onClick={() => fetchHistory(apiProjectIds)}>
                                        <Refresh />
                                    </IconButton>
                                </Tooltip>
                                {fetchingHistory && (
                                    <CircularProgress size={36} sx={{ position: 'absolute', top: 2, left: 2 }} />
                                )}
                            </Box>
                        </Stack>
                    )}
                </Stack>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : apiProjectIds.length === 0 ? (
                    <Typography sx={{ color: 'text.secondary' }}>
                        No API projects found. Create a project via the Pagination API to see its history here.
                    </Typography>
                ) : (
                    <>
                        {/* --- Grafico a barre (reagisce al filtro progetto) --- */}
                        {filteredPlotData.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart
                                        data={filteredPlotData}
                                        margin={{ top: 20, right: 30, bottom: 20, left: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis allowDecimals={false} />
                                        <RTooltip />
                                        <Legend />
                                        <Bar dataKey="completed" stackId="a" fill={Primarytheme.palette.success.main} name="Completed" />
                                        <Bar dataKey="failed" stackId="a" fill={Primarytheme.palette.error.main} name="Failed" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {/* --- Log list con paginazione --- */}
                        {filteredTableData.length > 0 ? (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {filteredTableData.length} request{filteredTableData.length !== 1 ? 's' : ''}
                                    {projectFilter.length > 0 ? ' (filtered)' : ''}
                                </Typography>

                                {pagedData.map(item => (
                                    <LogEntry
                                        key={item.id}
                                        item={item}
                                        user={auth.userState}
                                        onCopy={(value) => setSnackbar({ open: true, message: `Copied: ${value}`, severity: 'success' })}
                                    />
                                ))}

                                {/* Paginazione */}
                                {totalPages > 1 && (
                                    <Stack direction="row" justifyContent="right" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            Page {page + 1} of {totalPages}
                                        </Typography>

                                        <Button
                                            size="small"
                                            disabled={page === 0}
                                            onClick={() => setPage(p => p - 1)}
                                        >
                                            Previous
                                        </Button>

                                        <Button
                                            size="small"
                                            disabled={page >= totalPages - 1}
                                            onClick={() => setPage(p => p + 1)}
                                        >
                                            Next
                                        </Button>

                                    </Stack>
                                )}
                            </Box>
                        ) : (
                            !fetchingHistory && (
                                <Typography sx={{ color: 'text.secondary' }}>
                                    No API requests found in the last 13 months.
                                </Typography>
                            )
                        )}
                    </>
                )}
            </Paper>

            {/* ============================================================ */}
            {/* Snackbar per feedback                                        */}
            {/* ============================================================ */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
